import {
  useCallback,
  useEffect,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject
} from 'react';

import { getPanelResizeBounds } from './getPanelResizeBounds';
import {
  LEFT_PANEL_MIN_WIDTH,
  RESIZE_ENABLED_MIN_WIDTH,
  RIGHT_PANEL_MIN_WIDTH
} from './panelResizeConstants';
import {
  loadSavedLeftPanelWidth,
  saveLeftPanelWidth
} from './panelResizeStorage';

type UseHorizontalPanelResizeParams = {
  containerRef: RefObject<HTMLDivElement | null>;
  leftPanelRef: RefObject<HTMLDivElement | null>;
  canPersistWidth?: boolean;
};

export function useHorizontalPanelResize({
  containerRef,
  leftPanelRef,
  canPersistWidth = false
}: UseHorizontalPanelResizeParams) {
  const [leftPanelWidth, setLeftPanelWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isResizeEnabled, setIsResizeEnabled] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(`(min-width: ${RESIZE_ENABLED_MIN_WIDTH}px)`).matches
  );

  const clampWidth = useCallback(
    (width: number) => {
      const containerWidth = containerRef.current?.clientWidth ?? 0;
      const rightPanelMinWidth = isResizeEnabled ? RIGHT_PANEL_MIN_WIDTH : 0;
      const { min, max } = getPanelResizeBounds(
        containerWidth,
        rightPanelMinWidth
      );

      return Math.max(min, Math.min(max, width));
    },
    [containerRef, isResizeEnabled]
  );

  useEffect(() => {
    if (!isResizeEnabled || !canPersistWidth) return;

    const savedWidth = loadSavedLeftPanelWidth();
    if (savedWidth == null) return;

    const container = containerRef.current;
    if (!container) return;

    const restoreSavedWidth = () => {
      setLeftPanelWidth(currentWidth => {
        if (currentWidth != null) return currentWidth;
        return clampWidth(savedWidth);
      });
    };

    if (container.clientWidth > 0) {
      restoreSavedWidth();
      return;
    }

    const observer = new ResizeObserver(() => {
      if (container.clientWidth <= 0) return;
      restoreSavedWidth();
      observer.disconnect();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [canPersistWidth, clampWidth, containerRef, isResizeEnabled]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(min-width: ${RESIZE_ENABLED_MIN_WIDTH}px)`
    );
    const handleChange = (event: MediaQueryListEvent) => {
      setIsResizeEnabled(event.matches);
      if (!event.matches) {
        setLeftPanelWidth(null);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (leftPanelWidth == null) return;

    const handleWindowResize = () => {
      setLeftPanelWidth(currentWidth => {
        if (currentWidth == null) return currentWidth;
        const clampedWidth = clampWidth(currentWidth);
        saveLeftPanelWidth(clampedWidth);
        return clampedWidth;
      });
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [clampWidth, leftPanelWidth]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!isResizeEnabled) return;

      event.preventDefault();
      const currentWidth =
        leftPanelWidth ??
        leftPanelRef.current?.offsetWidth ??
        LEFT_PANEL_MIN_WIDTH;

      setIsResizing(true);
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const startX = event.clientX;
      const startWidth = currentWidth;
      let latestWidth = clampWidth(startWidth);

      setLeftPanelWidth(latestWidth);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - startX;
        latestWidth = clampWidth(startWidth + delta);
        setLeftPanelWidth(latestWidth);
      };

      const handlePointerUp = () => {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        saveLeftPanelWidth(latestWidth);
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [clampWidth, isResizeEnabled, leftPanelRef, leftPanelWidth]
  );

  return {
    leftPanelWidth: isResizeEnabled ? leftPanelWidth : null,
    isResizing,
    isResizeEnabled,
    handlePointerDown
  };
}
